import { useState } from "react";
import { ArrowDownCircle, ArrowUpCircle, Settings2 } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import type { Ingredient } from "@/hooks/useIngredients";
import { ExpenseLinkFields, type ExpenseLinkDraft } from "@/components/expenses/ExpenseLinkFields";

type MovementType = "entry" | "exit" | "adjustment";

const movementTypes = [
  {
    type: "entry" as const,
    label: "Entrada",
    description: "Adicionar estoque",
    icon: ArrowDownCircle,
    color: "text-green-500",
    bgColor: "bg-green-500/10",
  },
  {
    type: "exit" as const,
    label: "Saída",
    description: "Remover estoque",
    icon: ArrowUpCircle,
    color: "text-red-500",
    bgColor: "bg-red-500/10",
  },
  {
    type: "adjustment" as const,
    label: "Ajuste",
    description: "Definir quantidade exata",
    icon: Settings2,
    color: "text-blue-500",
    bgColor: "bg-blue-500/10",
  },
];

interface StockMovementDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  ingredient: Ingredient | null;
  onSave: (
    type: MovementType,
    quantity: number,
    reason?: string,
    cost?: number,
    expense?: {
      title: string;
      category_id: string;
      supplier_id?: string;
      due_date?: string;
    }
  ) => Promise<void>;
}

export function StockMovementDialog({
  open,
  onOpenChange,
  ingredient,
  onSave,
}: StockMovementDialogProps) {
  const [movementType, setMovementType] = useState<MovementType>("entry");
  const [quantity, setQuantity] = useState("");
  const [reason, setReason] = useState("");
  const [cost, setCost] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  const [expenseDraft, setExpenseDraft] = useState<ExpenseLinkDraft>({
    enabled: false,
    title: "",
    categoryId: "",
    supplierId: undefined,
    dueDate: undefined,
  });

  const handleSubmit = async () => {
    const qty = parseFloat(quantity);
    if (isNaN(qty) || qty <= 0) return;

    const costValue = cost ? parseFloat(cost) : undefined;
    const shouldLinkExpense =
      movementType === "entry" && !!costValue && costValue > 0 && expenseDraft.enabled;
    const canSubmitExpense =
      !shouldLinkExpense ||
      (expenseDraft.title.trim().length > 0 && !!expenseDraft.categoryId);

    if (!canSubmitExpense) return;

    setIsSubmitting(true);
    try {
      await onSave(
        movementType,
        qty,
        reason || undefined,
        costValue,
        shouldLinkExpense
          ? {
              title: expenseDraft.title.trim(),
              category_id: expenseDraft.categoryId,
              supplier_id: expenseDraft.supplierId,
              due_date: expenseDraft.dueDate,
            }
          : undefined
      );
      // Reset form
      setQuantity("");
      setReason("");
      setCost("");
      setMovementType("entry");
      setExpenseDraft({ enabled: false, title: "", categoryId: "", supplierId: undefined, dueDate: undefined });
    } finally {
      setIsSubmitting(false);
    }
  };

  const getNewStock = () => {
    if (!ingredient) return 0;
    const qty = parseFloat(quantity) || 0;
    if (movementType === "entry") {
      return ingredient.current_stock + qty;
    } else if (movementType === "exit") {
      return Math.max(0, ingredient.current_stock - qty);
    } else {
      return qty;
    }
  };

  if (!ingredient) return null;

  const costValue = cost ? parseFloat(cost) : 0;
  const showExpenseLink = movementType === "entry" && costValue > 0;
  const canSubmitExpense =
    !showExpenseLink ||
    !expenseDraft.enabled ||
    (expenseDraft.title.trim().length > 0 && !!expenseDraft.categoryId);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Movimentação de Estoque</DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          {/* Ingredient Info */}
          <div className="p-3 rounded-lg bg-muted/50">
            <p className="font-medium">{ingredient.name}</p>
            <p className="text-sm text-muted-foreground">
              Estoque atual: {ingredient.current_stock} {ingredient.unit}
            </p>
          </div>

          {/* Movement Type Selection */}
          <div className="space-y-2">
            <Label>Tipo de Movimentação</Label>
            <div className="grid grid-cols-3 gap-2">
              {movementTypes.map((mt) => (
                <button
                  key={mt.type}
                  type="button"
                  onClick={() => setMovementType(mt.type)}
                  className={cn(
                    "flex flex-col items-center gap-1 p-3 rounded-lg border-2 transition-all",
                    movementType === mt.type
                      ? `border-primary ${mt.bgColor}`
                      : "border-muted hover:border-primary/50"
                  )}
                >
                  <mt.icon className={cn("h-5 w-5", mt.color)} />
                  <span className="text-xs font-medium">{mt.label}</span>
                </button>
              ))}
            </div>
          </div>

          {/* Quantity */}
          <div className="space-y-2">
            <Label htmlFor="quantity">
              {movementType === "adjustment" ? "Nova Quantidade" : "Quantidade"} *
            </Label>
            <div className="flex items-center gap-2">
              <Input
                id="quantity"
                type="number"
                step="0.01"
                min="0"
                placeholder="0"
                value={quantity}
                onChange={(e) => setQuantity(e.target.value)}
                className="flex-1"
              />
              <span className="text-sm text-muted-foreground w-12">
                {ingredient.unit}
              </span>
            </div>
          </div>

          {/* New Stock Preview */}
          {quantity && (
            <div className="p-3 rounded-lg bg-primary/5 border border-primary/20">
              <p className="text-sm">
                <span className="text-muted-foreground">Novo estoque: </span>
                <span className="font-medium">
                  {getNewStock().toFixed(2)} {ingredient.unit}
                </span>
              </p>
            </div>
          )}

          {/* Cost (for entries) */}
          {movementType === "entry" && (
            <div className="space-y-2">
              <Label htmlFor="cost">Custo Total da Compra *</Label>
              <Input
                id="cost"
                type="number"
                step="0.01"
                min="0"
                placeholder="Ex: 150.00"
                value={cost}
                onChange={(e) => setCost(e.target.value)}
              />
              {cost && quantity && parseFloat(quantity) > 0 && (
                <p className="text-xs text-muted-foreground">
                  Custo unitário: R$ {(parseFloat(cost) / parseFloat(quantity)).toFixed(2)} / {ingredient.unit}
                </p>
              )}
            </div>
          )}

          {showExpenseLink && (
            <div className="rounded-lg border p-3">
              <ExpenseLinkFields
                value={expenseDraft}
                onChange={(next) => {
                  if (!expenseDraft.enabled && next.enabled && !next.title) {
                    setExpenseDraft({
                      ...next,
                      title: reason?.trim()
                        ? `Compra estoque - ${ingredient.name} (${reason.trim()})`
                        : `Compra estoque - ${ingredient.name}`,
                    });
                    return;
                  }
                  setExpenseDraft(next);
                }}
                checkboxLabel="Gerar conta a pagar (DESPESA)"
                titleLabel="Título da despesa"
              />
              {expenseDraft.enabled && !canSubmitExpense && (
                <p className="mt-2 text-xs text-muted-foreground">
                  Informe ao menos o título e a categoria.
                </p>
              )}
            </div>
          )}

          {/* Reason */}
          <div className="space-y-2">
            <Label htmlFor="reason">Motivo (opcional)</Label>
            <Textarea
              id="reason"
              placeholder="Ex: Compra, inventário, perda..."
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              rows={2}
            />
          </div>
        </div>

        <DialogFooter>
          <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
            Cancelar
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={isSubmitting || !quantity || parseFloat(quantity) <= 0 || !canSubmitExpense}
          >
            {isSubmitting ? "Salvando..." : "Confirmar"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
